/**
 * AudioPlayer primitive (Inc-1 extraction)
 *
 * Dumb playback unit: given an audio element and a chunk, plays it and
 * returns a Promise that resolves on the `ended` event, rejects on
 * error/abandon. No cursor logic, no card logic, no advancement.
 *
 * Also exports the manual/auto lookahead gate helper (BOU-1429) so the
 * gate lives alongside the primitive without the primitive owning it.
 */

// Watchdog & circuit breaker constants (mirrored from useUserAudioQueue).
const STALL_TIMEOUT_MS = 8000;       // 8s of no currentTime progress = stalled
const MAX_CHUNK_DURATION_MS = 120000; // 2min absolute ceiling per chunk
const WATCHDOG_INTERVAL_MS = 2000;   // Poll every 2s

/**
 * Play a single audio chunk on the given audio element and return a Promise
 * that resolves when the `ended` event fires, or rejects on:
 *   - MediaError (decode/network failure)
 *   - StallError (no currentTime progress for STALL_TIMEOUT_MS)
 *   - TimeoutError (chunk exceeds MAX_CHUNK_DURATION_MS)
 *   - AbortError  (isProcessing flipped false by stopQueuePlayback)
 *   - NotAllowedError (autoplay blocked)
 *
 * The audio element must already have its src assigned and load() called
 * before this function is called. This function only attaches event
 * listeners, starts the watchdog/timeout, and calls play().
 *
 * @param {HTMLAudioElement} audio        - The audio element to play.
 * @param {object}           chunk        - Chunk metadata (for logging).
 * @param {number}           chunkIndex   - 0-based index in the current batch (for logging).
 * @param {number}           totalChunks  - Total chunks in the current batch (for logging).
 * @param {object}           session      - The audio session (reads isProcessing).
 * @param {Function}         onPlaying    - Optional callback fired on the `playing` event.
 * @param {Function}         onEnded      - Optional callback fired just before resolve (after ended).
 * @param {Function}         onBlocked    - Optional callback when NotAllowedError fires.
 * @returns {Promise<void>}               - Resolves on ended, rejects on any failure.
 */
export function playChunk({
  audio,
  chunkIndex,
  totalChunks,
  session,
  onPlaying,
  onEnded: onEndedCb,
  onBlocked,
}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let lastCurrentTime = 0;
    let lastProgressTime = Date.now();
    let watchdogTimer = null;
    let hardTimeoutTimer = null;

    // Guard: only first resolution/rejection takes effect, cleans up all timers
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      clearInterval(watchdogTimer);
      clearTimeout(hardTimeoutTimer);
      cleanup();
      fn(value);
    };

    const onEnded = () => {
      console.log(`🎵 [USER_QUEUE] Chunk ${chunkIndex + 1}/${totalChunks} finished playing`);
      onEndedCb?.();
      settle(resolve);
    };

    const onError = () => {
      const mediaError = audio.error;
      const errorCode = mediaError?.code;
      const errorMessage = mediaError?.message || 'Unknown error';
      console.error(`🎵 [USER_QUEUE] Error playing chunk ${chunkIndex + 1}/${totalChunks}: code=${errorCode} message=${errorMessage}`);
      const err = new Error(errorMessage);
      err.name = errorCode === 4 ? 'NotSupportedError' : 'MediaError';
      err.code = errorCode;
      settle(reject, err);
    };

    const onLoadedMetadata = () => {
      try {
        audio.currentTime = 0;
      } catch {
        // Ignore; playback will still start from the new resource default.
      }
    };

    const handlePlaying = () => {
      lastCurrentTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
      lastProgressTime = Date.now();
      onPlaying?.();
    };

    const cleanup = () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('playing', handlePlaying);
    };

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('playing', handlePlaying);

    // Watchdog: poll currentTime to detect stalls and external cancellation
    watchdogTimer = setInterval(() => {
      // Check if stopQueuePlayback was called
      if (!session.isProcessing) {
        const err = new Error('Playback aborted');
        err.name = 'AbortError';
        settle(reject, err);
        return;
      }

      const now = Date.now();
      const currentTime = audio.currentTime;
      if (currentTime > lastCurrentTime) {
        lastCurrentTime = currentTime;
        lastProgressTime = now;
      } else if (now - lastProgressTime > STALL_TIMEOUT_MS) {
        console.warn(`🎵 [USER_QUEUE] Chunk ${chunkIndex + 1}/${totalChunks} stalled (no progress for ${STALL_TIMEOUT_MS}ms)`);
        const err = new Error(`Audio stalled for ${STALL_TIMEOUT_MS}ms`);
        err.name = 'StallError';
        settle(reject, err);
      }
    }, WATCHDOG_INTERVAL_MS);

    // Hard timeout: absolute ceiling per chunk
    hardTimeoutTimer = setTimeout(() => {
      console.warn(`🎵 [USER_QUEUE] Chunk ${chunkIndex + 1}/${totalChunks} exceeded max duration (${MAX_CHUNK_DURATION_MS}ms)`);
      const err = new Error(`Chunk exceeded ${MAX_CHUNK_DURATION_MS}ms maximum`);
      err.name = 'TimeoutError';
      settle(reject, err);
    }, MAX_CHUNK_DURATION_MS);

    audio.play().catch(err => {
      if (err.name === 'NotAllowedError') {
        console.warn(`🎵 [USER_QUEUE] Autoplay blocked for chunk ${chunkIndex + 1}/${totalChunks}. User interaction required.`);
        onBlocked?.();
      } else {
        console.error(`🎵 [USER_QUEUE] Failed to start playback for chunk ${chunkIndex + 1}/${totalChunks}:`, err);
      }
      settle(reject, err);
    });
  });
}

/**
 * BOU-1809: gapless variant of playChunk backed by the Web Audio scheduler.
 *
 * Decodes `arrayBuffer` and schedules it back-to-back with the previous segment
 * on the scheduler's shared AudioContext timeline. Unlike playChunk (which
 * resolves on `ended`), this resolves as soon as the segment has STARTED playing
 * — that early return lets the caller decode + schedule the NEXT segment while
 * this one is still playing, which is what eliminates the inter-segment gap.
 *
 * Rejects with the decode/schedule error (or AbortError if playback was stopped)
 * so the caller can fall back to the HTMLAudioElement path for this segment; in
 * that case behavior is unchanged from before BOU-1809.
 *
 * Callback parity with playChunk: onPlaying fires when the segment becomes
 * audible; onEnded fires when it finishes. onBlocked is unused — the caller only
 * takes this path when the AudioContext is already running (post-unlock), so a
 * Web Audio segment never hits the autoplay-gesture gate.
 *
 * @returns {Promise<void>} resolves when the segment starts; rejects on failure.
 */
export function playChunkGapless({
  scheduler,
  arrayBuffer,
  chunkIndex,
  totalChunks,
  session,
  playGeneration,
  playStopEpoch,
  playbackGroup,
  onPlaying,
  onEnded,
}) {
  // [Codex] Consider the segment live only while the session is processing AND
  // still on the generation captured when this batch started. A teardown (user /
  // campaign switch) bumps session.generation; a fresh processor can flip
  // isProcessing back to true on the SAME session object while decodeAudioData is
  // pending, so isProcessing alone would let a stale chunk schedule into the new
  // session. The generation check pins it to the original run.
  const stillLive = () => {
    const preservingPlaybackGroup =
      session.preservePlayerVoiceUntilEnd
      && playbackGroup === 'player_voice';
    return session.isProcessing
      && (playGeneration === undefined || session.generation === playGeneration)
      && (
        playStopEpoch === undefined
        || session.stopEpoch === playStopEpoch
        || preservingPlaybackGroup
      );
  };
  return new Promise((resolve, reject) => {
    if (!stillLive()) {
      const err = new Error('Playback aborted');
      err.name = 'AbortError';
      reject(err);
      return;
    }
    let startedResolved = false;
    scheduler
      .schedule(arrayBuffer, {
        playbackGroup,
        // Re-checked after decodeAudioData so stop/skip/teardown during decode
        // cannot schedule stale audio.
        shouldSchedule: stillLive,
        onStart: () => {
          console.log(`🎵 [USER_QUEUE] Chunk ${chunkIndex + 1}/${totalChunks} started (gapless)`);
          onPlaying?.();
          if (!startedResolved) {
            startedResolved = true;
            resolve();
          }
        },
        onEnd: () => {
          console.log(`🎵 [USER_QUEUE] Chunk ${chunkIndex + 1}/${totalChunks} finished playing (gapless)`);
          onEnded?.();
        },
        // [Codex] If stop()/reset lands during the pre-start cushion, the segment
        // never starts and onStart never fires — reject here so playQueueChunks
        // hits its AbortError recovery instead of awaiting forever.
        onAbort: () => {
          if (!startedResolved) {
            startedResolved = true;
            const err = new Error('Playback aborted');
            err.name = 'AbortError';
            reject(err);
          }
        },
      })
      .catch((err) => {
        console.warn(`🎵 [USER_QUEUE] Gapless schedule failed for chunk ${chunkIndex + 1}/${totalChunks}:`, err);
        reject(err);
      });
  });
}

/**
 * BOU-1429: client-side card-boundary gate (manual advance only).
 *
 * Given the current flat queue and playback mode, returns { chunksToPlay,
 * gatedTail } — the prefix playable at the current cursor and the tail that
 * must wait. The caller is responsible for:
 *   - Playing chunksToPlay via playChunk (or the queue processor)
 *   - Releasing gatedTail's dedup IDs so the next fetch re-serves them
 *
 * No advancement occurs here. The gate purely partitions the queue.
 *
 * @param {object[]} queue               - Current flat playback queue.
 * @param {boolean}  autoCardAdvancement - If true, gate is disabled (audio leads display).
 * @param {number|null} currentCardIndex - The displayed card cursor (for gating in manual mode).
 * @returns {{ chunksToPlay: object[], gatedTail: object[] }}
 */
export function partitionQueueAtCardBoundary(queue, autoCardAdvancement, currentCardIndex) {
  const manualGate = autoCardAdvancement === false;
  const cursor = currentCardIndex;
  let take = queue.length;
  if (manualGate && typeof cursor === 'number') {
    take = 0;
    while (
      take < queue.length
      && !(typeof queue[take].card_index === 'number' && queue[take].card_index > cursor)
    ) {
      take += 1;
    }
  }
  return {
    chunksToPlay: queue.slice(0, take),
    gatedTail: queue.slice(take),
  };
}
